import {
  streamerHistorySchema,
  type HistoryMonth,
  type StreamerHistory,
} from '@growup/streaming-shared';
import { Card, Notice, Table, type Column } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf } from '../api';
import { formatDate } from '../format';
import { formatCount, formatMinutes } from './labels';

const monthFormat = new Intl.DateTimeFormat('pt-PT', { timeZone: 'UTC', month: 'short' });

/** `2026-03` → `mar. 2026`. */
export function formatMonth(month: string): string {
  return `${monthFormat.format(new Date(`${month}-15T12:00:00Z`))} ${month.slice(0, 4)}`;
}

const change = (n: number) => (n > 0 ? `+${formatCount(n)}` : formatCount(n));

const SOURCE_LABEL: Record<HistoryMonth['source'], string> = {
  sullygnome: 'SullyGnome',
  twitch: 'Twitch',
};

const MONTH_COLUMNS: Column<HistoryMonth>[] = [
  { key: 'month', header: 'Mês', cell: (m) => formatMonth(m.month) },
  {
    key: 'live',
    header: 'Em direto',
    align: 'right',
    cell: (m) => (m.hasData ? formatMinutes(m.minutesLive) : 'sem dados'),
  },
  {
    key: 'streams',
    header: 'Transmissões',
    align: 'right',
    cell: (m) => (m.hasData ? formatCount(m.streams) : '—'),
  },
  {
    key: 'viewers',
    header: 'Média · pico',
    align: 'right',
    cell: (m) => (m.hasData ? `${formatCount(m.avgViewers)} · ${formatCount(m.peakViewers)}` : '—'),
  },
  {
    key: 'followers',
    header: 'Seguidores',
    align: 'right',
    cell: (m) => (m.followersGained === null ? '—' : change(m.followersGained)),
  },
  { key: 'source', header: 'Fonte', cell: (m) => SOURCE_LABEL[m.source] },
];

/**
 * Historico mes a mes de um streamer (`streaming.manage`), no separador
 * Numeros da ficha: SullyGnome (recolha unica) e depois as nossas leituras da
 * Twitch (docs/streaming, "Historico do SullyGnome").
 */
export function StreamerHistoryCard({ streamerId }: { streamerId: string }) {
  const [data, setData] = useState<StreamerHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson(`/api/streaming/streamers/${streamerId}/history`, streamerHistorySchema)
      .then((h) => {
        setError(null);
        setData(h);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [streamerId]);

  return (
    <Card headingLevel={2} title="Mês a mês">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {data === null ? null : data.months.length === 0 ? (
        <p className="text-gu-ink-muted">
          Ainda sem histórico: nem recolha do SullyGnome nem leituras da Twitch deste canal.
        </p>
      ) : (
        <HistoryBody data={data} />
      )}
    </Card>
  );
}

function HistoryBody({ data }: { data: StreamerHistory }) {
  const max = Math.max(1, ...data.months.map((m) => m.minutesLive));
  const first = data.months[0];
  const last = data.months[data.months.length - 1];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex h-32 items-end gap-[2px] border-b border-gu-line" role="list">
          {data.months.map((m) => {
            const label = `${formatMonth(m.month)}: ${
              m.hasData ? formatMinutes(m.minutesLive) : 'sem dados'
            } (${SOURCE_LABEL[m.source]})`;
            return (
              <div
                key={m.month}
                role="listitem"
                aria-label={label}
                title={label}
                className="group flex h-full flex-1 items-end hover:bg-gu-ink/5"
              >
                {m.hasData ? (
                  <div
                    className={`w-full rounded-t bg-gu-accent group-hover:opacity-80 ${
                      m.source === 'sullygnome' ? 'opacity-50' : ''
                    }`}
                    style={{ height: `${String((m.minutesLive / max) * 100)}%` }}
                  />
                ) : (
                  <div className="w-full border-t-2 border-dashed border-gu-line" />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-caption text-gu-ink-muted">
          <span>{first ? formatMonth(first.month) : ''}</span>
          <span>máx. {formatMinutes(max)} · SullyGnome mais claro</span>
          <span>{last ? formatMonth(last.month) : ''}</span>
        </div>
      </div>
      {data.ranges.length > 0 && data.importedAt ? (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {data.ranges.map((r) => (
            <div key={r.days} className="flex flex-col gap-1 border-l-2 border-gu-accent pl-3">
              <dt className="text-caption text-gu-ink-muted">
                {r.days} dias até {formatDate(data.importedAt ?? '')}
              </dt>
              <dd>
                {r.hasData
                  ? `${formatMinutes(r.minutesLive)} em direto · ${formatMinutes(r.watchMinutes)} vistas · ${change(r.followersGained)} seguidores`
                  : 'sem dados no SullyGnome'}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      <Table
        caption="Histórico mês a mês"
        columns={MONTH_COLUMNS}
        rows={[...data.months].reverse()}
        rowKey={(m) => m.month}
        empty="Sem histórico."
      />
    </div>
  );
}
